import { MutationObserver } from '@tanstack/query-core'
import { reaction } from 'mobx'
import { addDisposer, types } from 'mobx-state-tree'

import { TrackedChangeInfo } from '#choo-app/lib/editor/plugins/change-tracking/helpers'
import {
  getLocalChanges,
  getSelectedId,
  getTrackedChanges,
  selectTrackedChange,
} from '#choo-app/lib/editor/plugins/change-tracking/markChanges'
import {
  TrackedChange,
  TrackedChangeStatus,
  TrackedChangeStatusOptions,
  TrackedChangeType,
  TrackedChangeTypeOptions,
} from '#components/ChangeTracking/types'
import { getRootStore } from '#state/models/getRootStore'
import {
  removeProposedAdditionMarkAndContent,
  removeProposedDeletionMark,
  updateTrackedChangeMark,
} from '#util'
import { DatadogClient } from '#util/datadog'
import { extractTsRestSuccess } from '#util/extractTsRest'

import { BaseModel } from '../BaseModel'
import { IsoDate } from '../IsoDate'
import { TrackedChangeEventPayload } from '../SocketManager/helpers'

const ddLog = DatadogClient.getInstance()

export const TrackedChangeModel = BaseModel.named('TrackedChangeModel')
  .props({
    id: types.identifier,
    scriptId: types.string,
    snippet: types.maybeNull(types.string),
    createdAt: IsoDate,
    updatedAt: IsoDate,
    changeType: types.enumeration<TrackedChangeType>(TrackedChangeTypeOptions),
    status: types.enumeration<TrackedChangeStatus>(TrackedChangeStatusOptions),
    creator: types.model({
      id: types.string,
      name: types.string,
      avatar: types.maybeNull(types.string),
    }),
    reviewer: types.maybeNull(
      types.model({
        id: types.string,
        name: types.string,
        avatar: types.maybeNull(types.string),
      }),
    ),
    reviewedAt: types.maybeNull(IsoDate),
    replies: types.array(
      BaseModel.named('TrackedChangeReplyModel').props({
        id: types.identifier,
        text: types.string,
        createdAt: IsoDate,
        updatedAt: IsoDate,
        creator: types.model({
          id: types.string,
          name: types.string,
          avatar: types.maybeNull(types.string),
        }),
      }),
    ),
  })
  .views((self) => ({
    /**
     * Use the snippet present in the document, falling back to what's saved in
     * the api
     */
    get latestSnippet(): string | null {
      const editorState = getRootStore(self).currentScript?.pmEditor.editorState
      if (!editorState) return self.snippet

      const pluginChanges = getTrackedChanges(editorState)
      const currentChanges = pluginChanges.get(self.id) ?? []
      if (currentChanges.length === 0) return self.snippet
      return currentChanges.map((c) => c.snippet).join('...')
    },
    get isOrphaned(): boolean {
      const changeTrackingData =
        getRootStore(self).currentScript?.changeTrackingData
      if (!changeTrackingData) return false
      return !changeTrackingData.trackedChangeMapFromDocument.has(self.id)
    },
  }))

export const ChangeTrackingData = BaseModel.named('ChangeTrackingData')
  .props({
    trackedChangeMap: types.map(TrackedChangeModel),
  })
  .views((self) => ({
    get selectedChangeId(): string | null {
      const editorState = getRootStore(self).currentScript?.pmEditor.editorState
      if (!editorState) return null
      return getSelectedId(editorState) ?? null
    },
    get sortedChanges() {
      return Array.from(self.trackedChangeMap.values()).sort(
        (a, b) => a.createdAt.valueOf() - b.createdAt.valueOf(),
      )
    },
    // orphans are displayed as reviewed
    get proposedChanges() {
      return this.sortedChanges.filter((c) => !c.reviewedAt && !c.isOrphaned)
    },
    get reviewedChanges() {
      return this.sortedChanges.filter((c) => c.reviewedAt)
    },
    get hasNoProposedChanges() {
      return this.proposedChanges.length === 0
    },
    get hasNoReviewedChanges() {
      return this.reviewedChanges.length === 0
    },
    get trackedChangeMapFromDocument(): Map<string, TrackedChangeInfo[]> {
      const editorState = getRootStore(self).currentScript?.pmEditor.editorState
      if (!editorState) return new Map()
      return getTrackedChanges(editorState)
    },
    get localChanges(): Set<string> {
      const editorState = getRootStore(self).currentScript?.pmEditor.editorState
      if (!editorState) return new Set()
      return getLocalChanges(editorState)
    },
    get trackedChangesFromDocument() {
      return Array.from(this.trackedChangeMapFromDocument.values())
    },
    get unsavedTrackedChanges() {
      return this.trackedChangesFromDocument.filter(
        (change) =>
          !self.trackedChangeMap.has(change[0].id) &&
          this.localChanges.has(change[0].id),
      )
    },
  }))
  .actions((self) => ({
    ingestChange(payload: TrackedChange) {
      self.trackedChangeMap.put(payload)
    },
    ingestList(payload: TrackedChange[]) {
      payload.forEach(this.ingestChange)
    },
    pluckChangeFromMap(id: string) {
      self.trackedChangeMap.delete(id)
    },
    setSelectedChangeId(id: string) {
      getRootStore(self).currentScript?.pmEditor.invoke(selectTrackedChange(id))
    },
  }))
  .actions((self) => ({
    async loadChange(params: { changeId: string; scriptId: string }) {
      const { body } = await extractTsRestSuccess(
        self.scrapi.scripts.getTrackedChange({ params }),
        200,
      )
      self.ingestChange(body)
    },

    async loadTrackedChanges(scriptId: string) {
      const opts = { params: { scriptId } }
      const fn = self.scrapi.scripts.listTrackedChanges(opts)
      const resp = await extractTsRestSuccess(fn, 200)
      self.ingestList(resp.body.results)
    },
    async deleteChange({
      changeId,
      scriptId,
      isAddition,
    }: {
      changeId: string
      scriptId: string
      isAddition: boolean
    }) {
      const { currentScript } = getRootStore(self)
      if (scriptId !== currentScript?.id) return

      const command = isAddition
        ? removeProposedAdditionMarkAndContent(changeId)
        : removeProposedDeletionMark(changeId)

      currentScript.pmEditor.invoke(command)
    },
    async approveChange({
      changeId,
      scriptId,
    }: {
      changeId: string
      scriptId: string
    }) {
      const isAddition =
        self.trackedChangeMap.get(changeId)?.changeType === 'addition'

      const { currentScript } = getRootStore(self)
      if (scriptId !== currentScript?.id) return

      currentScript.pmEditor.invoke(
        updateTrackedChangeMark({
          changeId,
          oldStatus: 'proposed',
          status: 'approved',
          isAddition,
        }),
      )
    },
    async rejectChange({
      changeId,
      scriptId,
    }: {
      changeId: string
      scriptId: string
    }) {
      const isAddition =
        self.trackedChangeMap.get(changeId)?.changeType === 'addition'

      const { currentScript } = getRootStore(self)
      if (scriptId !== currentScript?.id) return

      currentScript.pmEditor.invoke(
        updateTrackedChangeMark({
          changeId,
          oldStatus: 'proposed',
          status: 'rejected',
          isAddition,
        }),
      )
    },
    async unreviewChange({
      changeId,
      scriptId,
    }: {
      changeId: string
      scriptId: string
    }) {
      const isAddition =
        self.trackedChangeMap.get(changeId)?.changeType === 'addition'
      const oldStatus =
        (self.trackedChangeMap.get(changeId)?.status as TrackedChangeStatus) ??
        'approved'

      const { currentScript } = getRootStore(self)
      if (scriptId !== currentScript?.id) return

      currentScript.pmEditor.invoke(
        updateTrackedChangeMark({
          changeId,
          oldStatus,
          status: 'proposed',
          isAddition,
        }),
      )
    },

    async syncWithEditorState() {
      const script = getRootStore(self).currentScript
      if (!script?.pmEditor.editorState) return

      try {
        const newChanges = self.unsavedTrackedChanges

        if (newChanges.length === 0) return

        const queryClient = getRootStore(self).environment.queryClient
        const mutationCache = queryClient.getMutationCache()

        for (const changes of newChanges) {
          for (const change of changes) {
            const mutationKey = ['createTrackedChange', change.id]

            const existingMutation = mutationCache.find({ mutationKey })
            if (existingMutation?.state.status === 'pending') continue

            const mutationObserver = new MutationObserver(queryClient, {
              mutationKey,
              mutationFn: () =>
                script.scrapi.scripts.createTrackedChange({
                  params: { scriptId: script.id },
                  body: {
                    snippet: change.snippet,
                    id: change.id,
                    changeType: change.changeType,
                  },
                }),
            })

            try {
              await mutationObserver.mutate()
            } catch (error) {
              ddLog.error('Failed to persist tracked change', {
                changeId: change.id,
                error,
              })
            }
          }
        }
      } catch (error) {
        ddLog.error('Error syncing with editor state', { error })
      }
    },

    processSocketMessage(payload: TrackedChangeEventPayload) {
      const { scriptId, changeId } = payload
      switch (payload.eventType) {
        case 'TRACKED_CHANGE_ADDED':
        case 'TRACKED_CHANGE_REVIEWED':
        case 'TRACKED_CHANGE_UPDATED':
          this.loadChange({ scriptId, changeId })
          break
        case 'TRACKED_CHANGE_DELETED': {
          self.pluckChangeFromMap(changeId)
          break
        }
      }
    },
  }))
  .actions((self) => ({
    afterCreate() {
      // Set up reactive persistence: whenever new tracked changes appear in the
      // plugin state that aren't in our map of ones saved to the db, add 'em
      addDisposer(
        self,
        reaction(
          () => self.unsavedTrackedChanges.map((changes) => changes[0].id),
          (newChangeIds) => {
            if (!newChangeIds.length) return
            // Use setTimeout to avoid triggering during MST transaction
            setTimeout(() => {
              self.syncWithEditorState()
            }, 0)
          },
          {
            // Find a comfortable delay: can't be too short that you see tiny
            // fragments of text appear in the snippets, can't be too long that
            // people switch to another script and lose edits
            delay: 5000,
            // Only fire when the list actually changes. You need this because
            // the unpersistedChangeIds are in document order, so you can't just
            // do a pairwise comparison of ids
            equals: (a, b) => {
              const setA = new Set(a)
              const setB = new Set(b)
              return (
                setA.size === setB.size && [...setA].every((id) => setB.has(id))
              )
            },
          },
        ),
      )
    },
  }))
